import { Injectable } from '@angular/core';

import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, of, Subscriber } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { get } from 'scriptjs';

import { environment } from '../../../environments/environment';

import { SocialLogin } from '@capgo/capacitor-social-login';
import { AppTrackingTransparency } from 'capacitor-plugin-app-tracking-transparency';
import { SSOAuthResult, SSOLoginType } from '../../shared/models/interfaces/sso.interface';
import { IsApplicationService } from './is-application.service';

@Injectable({
  providedIn: 'root',
})
export class SSOFacebookService {
  isLoaded = new BehaviorSubject<boolean>(false);
  isLoading = false;

  constructor(private toastr: ToastrService, private isApplicationService: IsApplicationService) {}

  init(): Observable<boolean> {
    if (!this.isApplicationService.isApplication()) {
      return new Observable((observer) => {
        get('https://connect.facebook.net/' + environment.locale + '/sdk.js', () => {
          (window as any).fbAsyncInit = () => {
            FB.init({
              appId: environment.fb_id,
              cookie: true,
              xfbml: true,
              version: 'v17.0',
            });

            observer.next(true);
          };
        });
      });
    } else {
      return of(true);
    }
  }

  initialized() {
    if (!this.isLoaded.getValue()) {
      if (!this.isLoading) {
        return this.init().pipe(
          tap(() => {
            this.isLoading = false;
            this.isLoaded.next(true);
          })
        );
      } else {
        return this.isLoaded.pipe(
          filter((isLoaded) => !!isLoaded),
          take(1)
        );
      }
    } else {
      return of(true);
    }
  }

  login(): Observable<{
    ssoAuthResult: SSOAuthResult;
    loginType: SSOLoginType;
  }> {
    if (this.isApplicationService.isApplication()) {
      return this.loginFromApplication();
    } else {
      return this.loginFromDesktop();
    }
  }

  loginFromApplication(): Observable<{
    ssoAuthResult: SSOAuthResult;
    loginType: SSOLoginType;
  }> {
    return new Observable((observer) => {
      SocialLogin.initialize({
        facebook: {
          appId: environment.fb_id,
          clientToken: environment.fb_client_token,
        },
      }).then(() => {
        if (this.isApplicationService.isAppIos()) {
          AppTrackingTransparency.getStatus().then((status) => {
            // nnkitodo [later] il y a moyen de faire plus propre ici, idem pour le loginType qu'on passe partout
            if (status.status === 'authorized') {
              this.doFacebookApplicationLogin(observer, false);
            } else {
              this.doFacebookApplicationLogin(observer, true);
            }
          });
        } else {
          this.doFacebookApplicationLogin(observer, false);
        }
      });
    });
  }

  doFacebookApplicationLogin(
    observer: Subscriber<{
      ssoAuthResult: SSOAuthResult;
      loginType: SSOLoginType;
    }>,
    isLimited: boolean
  ) {
    SocialLogin.login({
      provider: 'facebook',
      options: {
        permissions: ['email', 'public_profile'],
        limitedLogin: isLimited,
      },
    }).then(
      (resLogin) => {
        if (resLogin?.result?.isLimitedLogin && resLogin?.result?.idToken) {
          observer.next({
            ssoAuthResult: {
              authResponse: {
                accessToken: resLogin.result.idToken,
              },
            },
            loginType: SSOLoginType.LIMITED,
          });
        } else if (resLogin?.result?.accessToken?.token) {
          observer.next({
            ssoAuthResult: {
              authResponse: {
                accessToken: resLogin.result.accessToken.token,
              },
            },
            loginType: SSOLoginType.DEFAULT,
          });
        } else {
          console.error(
            $localize`:@@erreur.erreur_produite:Une erreur s'est produite` + '-errFBCO1'
          );
          this.toastr.warning(
            $localize`:@@erreur.erreur_produite:Une erreur s'est produite` + '-errFBCO1'
          );
          observer.error('FBERROR: no resLogin received');
        }
      },
      (err) => {
        console.error(err);
        this.toastr.warning(
          $localize`:@@erreur.erreur_produite:Une erreur s'est produite` + '-errFBCO2'
        );
        observer.error(err);
      }
    );
  }

  loginFromDesktop(): Observable<{
    ssoAuthResult: SSOAuthResult;
    loginType: SSOLoginType;
  }> {
    return new Observable((observer) => {
      this.initialized().subscribe(() => {
        FB.getLoginStatus((resStatus) => {
          if (resStatus.status && resStatus.status === 'connected' && resStatus.authResponse) {
            observer.next({
              ssoAuthResult: resStatus as SSOAuthResult,
              loginType: SSOLoginType.DEFAULT,
            });
          } else {
            FB.login(
              (resLogin) => {
                if (resLogin && resLogin.authResponse) {
                  observer.next({
                    ssoAuthResult: resLogin as SSOAuthResult,
                    loginType: SSOLoginType.DEFAULT,
                  });
                } else {
                  console.error($localize`errFBCO4-La popup a été fermée`);
                  observer.error($localize`errFBCO4-La popup a été fermée`);
                }
              },
              { scope: 'public_profile,email' }
            );
          }
        });
      });
    });
  }

  logoutFromApplication() {
    SocialLogin.initialize({
      facebook: {
        appId: environment.fb_id,
        clientToken: environment.fb_client_token,
      },
    }).then(() => {
      SocialLogin.isLoggedIn({
        provider: 'facebook',
      }).then((resStatus) => {
        if (resStatus.isLoggedIn) {
          SocialLogin.logout({
            provider: 'facebook',
          });
        }
      });
    });
  }

  logoutFromDesktop() {
    if (this.isLoaded.getValue()) {
      FB.getLoginStatus((resStatus) => {
        if (resStatus.status && resStatus.status === 'connected') {
          FB.logout(() => {});
          document.cookie =
            'fblo_' + environment.fb_id + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
          window.location.reload();
        }
      });
    }
  }
}
